/**
 * Headless workshop-modal host. Mounts every modal the BriefPanel
 * rows can open (Agenda / Map / Bring / Questionnaire / Doc /
 * Data Tables / Transcript) and wires each to the shared
 * `useWorkshopModals` Zustand store. Renders no UI itself — the
 * trigger rows live in `BriefPanel` (matching legacy `_legacy/wd.html`
 * which kept Brief + all modal triggers as one continuous list).
 *
 * Mount once per workshop dashboard, alongside `BriefPanel`. The two
 * components share `useWorkshopModals` so click → open is decoupled
 * from where the trigger renders.
 *
 * History: legacy `_legacy/wd.html` lines 336-374 wired
 * `onclick="openAgendaModal()"` etc. directly on `.brief-list-item`
 * rows. Earlier this React rebuild split those triggers into a
 * separate `ModalTriggers` panel — that's been collapsed back into
 * the Brief list to match legacy.
 */

import { modalContentByWorkshop } from "./modal-content";
import { resolveField } from "@/features/workshop/overrides/workshop-override-content";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { AgendaModal } from "./AgendaModal";
import { MapModal } from "./MapModal";
import { BringModal } from "./BringModal";
import { QuestionnaireModal } from "./QuestionnaireModal";
import { DocModal } from "./DocModal";
import { useWorkshopModals } from "./useWorkshopModals";
import { DataTablesModal } from "@/features/ws4-data-tables/DataTablesModal";
import { TranscriptPlayback } from "@/features/ws4-transcript/TranscriptPlayback";
import { PlaybackModal } from "@/features/ws4-playback/PlaybackModal";
import { CapturesModal } from "@/features/ws4-captures/CapturesModal";
import { QuestionnaireResultsModal } from "@/features/questionnaire-results/QuestionnaireResultsModal";
import { AnalysisModal } from "@/features/ws4-analysis/AnalysisModal";
import { ReportModal } from "@/features/ws4-report/ReportModal";
import { MeetCrewModal } from "@/components/shell/MeetCrewModal";
import { DecisionLogModal } from "./DecisionLogModal";
import { HawModal } from "../HawModal";
import { VersionHistoryModal } from "@/features/versioning/VersionHistoryModal";
import { CrossSearchModal } from "@/features/search/CrossSearchModal";
import { TraceabilityModal } from "@/features/traceability/TraceabilityModal";
import { InsightLibraryModal } from "@/features/insight-library/InsightLibraryModal";
import type { Workshop } from "@/content/workshops";

type ModalTriggersProps = {
  workshopId: string;
};

export function ModalTriggers({ workshopId }: ModalTriggersProps) {
  const active = useWorkshopModals((s) => s.active);
  const open = useWorkshopModals((s) => s.open);
  const close = useWorkshopModals((s) => s.close);
  const content = modalContentByWorkshop[workshopId as keyof typeof modalContentByWorkshop];
  // Power-user per-workshop overrides win over the authored defaults. With no
  // override set, `resolveField` returns each `content.*` unchanged.
  const override = useEffectiveWorkshopOverride(workshopId);

  if (!content) return null;

  return (
    <>
      <AgendaModal
        open={active === "agenda"}
        onOpenChange={(o) => (o ? open("agenda") : close())}
        workshopId={workshopId}
        agenda={resolveField(override, "agenda", content.agenda)}
      />
      <MapModal
        open={active === "map"}
        onOpenChange={(o) => (o ? open("map") : close())}
        workshopId={workshopId}
        map={resolveField(override, "map", content.map)}
      />
      <BringModal
        open={active === "bring"}
        onOpenChange={(o) => (o ? open("bring") : close())}
        workshopId={workshopId}
        bring={resolveField(override, "bring", content.bring)}
      />
      <QuestionnaireModal
        open={active === "questionnaire"}
        onOpenChange={(o) => (o ? open("questionnaire") : close())}
        workshopId={workshopId}
        questionnaire={resolveField(override, "questionnaire", content.questionnaire)}
      />
      <DocModal
        open={active === "doc"}
        onOpenChange={(o) => (o ? open("doc") : close())}
        workshopId={workshopId as Workshop["id"]}
      />
      <DataTablesModal
        open={active === "data-tables"}
        onOpenChange={(o) => (o ? open("data-tables") : close())}
        workshopId={workshopId}
      />
      <TranscriptPlayback
        open={active === "transcript"}
        onOpenChange={(o) => (o ? open("transcript") : close())}
        workshopId={workshopId}
      />
      <PlaybackModal
        open={active === "playback"}
        onOpenChange={(o) => (o ? open("playback") : close())}
        workshopId={workshopId}
      />
      <CapturesModal
        open={active === "captures"}
        onOpenChange={(o) => (o ? open("captures") : close())}
        workshopId={workshopId}
      />
      <QuestionnaireResultsModal
        open={active === "questionnaire-results"}
        onOpenChange={(o) => (o ? open("questionnaire-results") : close())}
        workshopId={workshopId}
      />
      <AnalysisModal
        open={active === "analysis"}
        onOpenChange={(o) => (o ? open("analysis") : close())}
        workshopId={workshopId}
      />
      <ReportModal
        open={active === "report"}
        onOpenChange={(o) => (o ? open("report") : close())}
        workshopId={workshopId}
      />
      <MeetCrewModal
        open={active === "meet-crew"}
        onClose={close}
      />
      <DecisionLogModal
        open={active === "decision-log"}
        onOpenChange={(o) => (o ? open("decision-log") : close())}
        workshopId={workshopId}
      />
      {/* Track A cross-cutting surfaces (CC-SEARCH / CC-LINKS / INSIGHT-LIB).
          Launched from HudHeader; span the whole program but mount here in
          the workshop modal host. */}
      <CrossSearchModal
        open={active === "cross-search"}
        onOpenChange={(o) => (o ? open("cross-search") : close())}
        workshopId={workshopId}
      />
      <TraceabilityModal
        open={active === "traceability"}
        onOpenChange={(o) => (o ? open("traceability") : close())}
        workshopId={workshopId}
      />
      <InsightLibraryModal
        open={active === "insight-library"}
        onOpenChange={(o) => (o ? open("insight-library") : close())}
        workshopId={workshopId}
      />
      <HawModal
        open={active === "haw"}
        onOpenChange={(o) => (o ? open("haw") : close())}
      />
      {/* Handshake + Stress-Test launch as routed pages from RunSequence
          (`/workshop/:id/handshake` / `.../stress-test`) so they own the
          full main band. No Dialog mounts here. */}
      {/* RT-VERSIONING — mounted last (Track B's disjoint region per the
          collision matrix). Reads its target from useVersionHistory, set
          by the per-entry Clock affordance in AssetLocker / Captures. */}
      <VersionHistoryModal
        open={active === "version-history"}
        onOpenChange={(o) => (o ? open("version-history") : close())}
      />
    </>
  );
}
